import type { CarPrefs } from "../store/persistence";
import { ParkAndRidePreference } from "../types/dtos/api/itinerary/itinerary-search-request.dto";

/** Pre-transit car access fields for itinerary search requests. */
export interface CarTransitAccessRequestFields {

    /**
     * When `true`, enables kiss-and-ride (MOTIS `CAR_DROPOFF`).
     * Omitted when personal car is off or train access is not selected.
     */
    "carDropOff"?: boolean;

    /**
     * Park-and-ride preferences sent when liaisons P+R are enabled.
     * Omitted when personal car is off or parking access is not selected.
     */
    "carParkAndRide"?: ParkAndRidePreference[];
}

/**
 * Maps a persisted park-and-ride preference id to the API enum value.
 *
 * @param preference Persisted preference from `carPrefsSlice`.
 * @returns Matching {@link ParkAndRidePreference} enum member.
 */
const toParkAndRidePreference = ( preference: CarPrefs["parkAndRidePreference"] ): ParkAndRidePreference => {
    if ( preference === "MAX_TRANSPORT" ) {
        return ParkAndRidePreference.MAX_TRANSPORT;
    }

    if ( preference === "MAX_CAR" ) {
        return ParkAndRidePreference.MAX_CAR;
    }

    if ( preference === "BALANCED" ) {
        return ParkAndRidePreference.BALANCED;
    }

    return ParkAndRidePreference.ALL;
};

/**
 * Maps persisted car preferences to itinerary search pre-transit car access fields.
 *
 * @param prefs Persisted car preferences from `carPrefsSlice`.
 * @returns Request fields for kiss-and-ride and/or park-and-ride; empty when personal car is off.
 */
export const toCarTransitAccessRequestFields = ( prefs: CarPrefs ): CarTransitAccessRequestFields => {
    if ( prefs.carUsageMode !== "personal_car" ) {
        return {};
    }

    const fields: CarTransitAccessRequestFields = {};

    if ( prefs.ptAccessModes.includes( "train" ) ) {
        fields.carDropOff = true;
    }

    if ( prefs.ptAccessModes.includes( "parking" ) ) {
        fields.carParkAndRide = [ toParkAndRidePreference( prefs.parkAndRidePreference ) ];
    }

    return fields;
};
